'use client'

import { Button } from '@/components/ui/button'
import { useCart } from '@/contexts/cart'
import { useProductTracking } from '@/hooks/use-tracking'
import { trackEvent } from '@/lib/tracking'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

/**
 * Interface representing a product.
 */
interface Product {
  /**
   * The unique identifier for the product.
   */
  id: string;

  /**
   * The name of the product.
   */
  name: string;

  /**
   * An array of image URLs for the product.
   */
  images: string[];

  /**
   * The unique identifier for the product variant.
   */
  variant_id: string;

  /**
   * The inventory quantity of the product variant.
   */
  inventory_quantity: number;

  metadata?: Record<string, string>;
}

export function AddToCartButton({ product }: { product: Product }) {
  const { cart, addItem } = useCart()
  const t = useTranslations('Cart.AddToCart')
  const { trackProductAddedToCart } = useProductTracking()

  const countInCart = useMemo(() => cart?.items?.filter(
    (item) => item.variant_id === product.variant_id
  ).reduce((acc, item) => acc + item.quantity, 0) ?? 0, [cart, product.variant_id])

  const isFullyInCart = useMemo(() => countInCart >= product.inventory_quantity, [countInCart, product.inventory_quantity])
  const hasStock = product.inventory_quantity > 0

  const handleAddToCart = () => {
    // Track the add to cart event
    trackProductAddedToCart({
      id: product.id,
      title: product.name,
      variant_id: product.variant_id,
      quantity: 1,
      store_id: product.metadata?.store_id
    })
    
    // Also track with detailed information
    trackEvent('Product Added', {
      product_id: product.id,
      product_name: product.name,
      variant_id: product.variant_id,
      quantity: 1,
      price: cart?.items?.find(item => item.variant_id === product.variant_id)?.unit_price,
      currency: cart?.region?.currency_code,
      store_id: product.metadata?.store_id,
      image_url: product.images[0],
      inventory_remaining: product.inventory_quantity - 1,
      cart_id: cart?.id
    })
    
    // Add the item to cart
    addItem(product.variant_id, 1, product.metadata)
  }

  return (
    <div className="flex gap-3 items-center">
      <Button onClick={handleAddToCart} disabled={isFullyInCart || !hasStock} className='flex-1'>
        {hasStock ? t('add') : t('outOfStock')}
      </Button>
      {countInCart > 0 && !isFullyInCart && (
        <div className="min-w-[80px] px-3 py-1.5 text-sm border rounded-md bg-muted/30 text-muted-foreground flex items-center justify-center">
          {countInCart} {t('added')}
        </div>
      )}
    </div>
  )
}
